import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Download, ShoppingBag, ArrowRight, Loader2,
  Receipt, ShieldCheck, FileSpreadsheet, Percent,
} from "lucide-react";
import { useDistributor } from "../context/DistributorContext";

export default function DistributorDashboardPage() {
  const { user, api, logout } = useDistributor();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/distributor/login?next=/distributor/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/distributors/me/orders").then((r) => setOrders(r.data.orders || [])).finally(() => setLoadingOrders(false));
  }, [user, api]);

  if (user === null || user === false) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }

  const BASE = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("dist_token") || "";
  // Authenticated downloads need the bearer header — we open a blob via fetch
  const downloadPriceList = async () => {
    const r = await fetch(`${BASE}/api/distributors/me/price-list.xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nibro-${(user.company || "distributeur").toLowerCase().replace(/[^a-z0-9-]+/g, "-")}-prijslijst.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-8 lg:py-12" data-testid="distributor-dashboard">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="label-uppercase">Distributeur Portal</div>
          <h1 className="font-display text-3xl font-bold text-zinc-900 sm:text-4xl">
            Welkom, {user.contact_name?.split(" ")[0] || user.company}
          </h1>
          <p className="mt-1 text-sm text-zinc-600">{user.company}</p>
        </div>
        <button
          type="button"
          onClick={logout}
          data-testid="dist-logout"
          className="inline-flex items-center gap-1.5 rounded border border-zinc-200 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-600 hover:bg-zinc-50"
        >
          <LogOut size={12} /> Uitloggen
        </button>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-md border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            <Percent size={11} /> Uw opslag op kostprijs
          </div>
          <div className="mt-2 font-display text-3xl font-bold text-nibro-sage-dark" data-testid="dist-markup-display">
            {user.markup_pct?.toFixed(1)}%
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Distributeur-prijs vervangt de winkelprijs op alle product pagina's.
          </div>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            <ShoppingBag size={11} /> Orders deze account
          </div>
          <div className="mt-2 font-display text-3xl font-bold text-zinc-900" data-testid="dist-order-count">
            {loadingOrders ? "…" : orders.length}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">Maximaal 100 recente orders.</div>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            <ShieldCheck size={11} /> Status
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
            ✓ Actief
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">
            Lid sinds {new Date(user.approved_at || user.created_at).toLocaleDateString("nl-NL")}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-md border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-900">
            <FileSpreadsheet size={16} className="text-nibro-sage-dark" /> Uw prijslijst
          </div>
          <p className="mt-2 text-xs text-zinc-600">
            Download een Excel met alle 1300+ SKUs en uw eigen distributeur-prijs per artikel.
            Geldig zolang uw opslagpercentage ongewijzigd blijft.
          </p>
          <button
            type="button"
            onClick={downloadPriceList}
            data-testid="dist-download-pricelist"
            className="mt-3 inline-flex items-center gap-2 rounded-md bg-nibro-sage-dark px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-nibro-sage"
          >
            <Download size={12} /> Download .xlsx
          </button>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-900">
            <ShoppingBag size={16} className="text-nibro-sage-dark" /> Bulk-bestellen
          </div>
          <p className="mt-2 text-xs text-zinc-600">
            Plak een SKU+aantal lijst (uit ERP of Excel) en zet alles met één klik
            in de winkelwagen — uw distributeur-prijs wordt automatisch toegepast.
          </p>
          <Link
            to="/bulk-order"
            data-testid="dist-bulk-link"
            className="mt-3 inline-flex items-center gap-2 rounded-md border border-nibro-sage-dark px-4 py-2 text-xs font-bold uppercase tracking-wide text-nibro-sage-dark hover:bg-nibro-sage-pale"
          >
            Open bulk-import <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Orders */}
      <div className="mt-6 rounded-md border border-zinc-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-zinc-900">
          <Receipt size={16} className="text-nibro-sage-dark" /> Order historie
        </div>
        {loadingOrders ? (
          <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin text-zinc-400" /></div>
        ) : orders.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-500">U heeft nog geen orders geplaatst.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="dist-orders-table">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <th className="py-2 pr-3">Ordernummer</th>
                  <th className="py-2 pr-3">Datum</th>
                  <th className="py-2 pr-3">Items</th>
                  <th className="py-2 pr-3 text-right">Subtotaal</th>
                  <th className="py-2 pr-3 text-right">Totaal</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_number} className="border-t border-zinc-100" data-testid={`dist-order-${o.order_number}`}>
                    <td className="py-2 pr-3 font-mono text-xs font-bold text-zinc-900">{o.order_number}</td>
                    <td className="py-2 pr-3 text-xs">{new Date(o.created_at).toLocaleDateString("nl-NL")}</td>
                    <td className="py-2 pr-3 text-xs text-zinc-700">{o.items?.length || 0} regel{o.items?.length === 1 ? "" : "s"}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">€{o.subtotal?.toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs font-bold">€{o.total?.toFixed(2)}</td>
                    <td className="py-2 pr-3 text-xs uppercase">{o.status || "verwerking"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
